import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import v1 from '../assets/img/values1.jpg';
import v2 from '../assets/img/values2.jpg';
import v3 from '../assets/img/values3.jpg';
import "../styles/Values.css";

//! ----------------------------> VALUES (auto-looping story: 1 -> 2 -> 3 -> 1 ...)
const VALUE_DURATION = 5000; // ms a value stays on screen before auto-advancing
const IMAGE_DURATION = 1.1;  // s for the image clip/scale hand-off
const TEXT_OUT = 0.45;       // s for the outgoing text
const TEXT_STAGGER = 0.02;   // s between outgoing lines
const EASE_IN_OUT = 'power4.inOut';
const EASE_OUT = 'expo.out';

const values = [
    { image: v1, title: 'Authenticity', text: ['Our commitment to real estate services', 'revolves around innovation and growth.', 'Every decision, every strategy, and', 'every process is developed to improve', 'client experience. We focus deeply on', 'fostering a culture where our agents', 'deliver solutions that bring value.'] },
    { image: v2, title: 'Empathy', text: ['We prioritize the ambitious goals of', 'our clients and ensure quality results.', 'Challenges are opportunities for us,', 'and we support our partners fully.', 'Every relationship is built on trust,', 'commitment, and a shared vision of', 'creating thriving, sustainable spaces.'] },
    { image: v3, title: 'Passion', text: ['With real estate decisions impacting', 'communities and the environment, we', 'are dedicated to sustainability and', 'long-term impact. We integrate eco-', 'friendly choices in each project and', 'aim to meet our clients needs while', 'protecting resources for the future.'] }
];

const FULL_CLIP = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
const RIGHT_EDGE_CLIP = 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)';
const LEFT_EDGE_CLIP = 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)';

const Values = () => {
    const sectionRef = useRef(null);
    const cursorRef = useRef(null);
    const cursorTextRef = useRef(null);
    const imageRefs = useRef([]);      // one stacked .img layer per value
    const highlightRefs = useRef([]);  // progress bar per value
    const titleRef = useRef(null);
    const lineRefs = useRef([]);

    const [activeIndex, setActiveIndex] = useState(0);
    const activeIndexRef = useRef(0);
    const directionRef = useRef('next'); // direction of the transition in flight
    const isAnimatingRef = useRef(false);
    const timerRef = useRef(null);
    const cursorMoveRef = useRef(null);
    const timelineRef = useRef(null);

    //! ================= >>> TRANSITION TO ANOTHER VALUE <<< ============
    const goTo = useCallback((nextIndex, direction = 'next') => {
        const current = activeIndexRef.current;
        if (isAnimatingRef.current || nextIndex === current) return;

        isAnimatingRef.current = true;
        directionRef.current = direction;
        clearTimeout(timerRef.current);

        const outgoing = imageRefs.current[current];
        const incoming = imageRefs.current[nextIndex];
        const sign = direction === 'next' ? 1 : -1;
        const textTargets = [titleRef.current, ...lineRefs.current].filter(Boolean);

        timelineRef.current?.kill();
        const tl = gsap.timeline({
            defaults: { overwrite: 'auto' },
            onComplete: () => {
                // hide the old layer and reset it for its next appearance
                gsap.set(outgoing, { autoAlpha: 0, zIndex: 0, clipPath: FULL_CLIP });
                gsap.set(outgoing.firstElementChild, { scale: 1, rotate: 0 });
                isAnimatingRef.current = false;
            },
        });

        //? text slides out through its clip rows
        tl.to(textTargets, {
            yPercent: -110 * sign,
            opacity: 0,
            duration: TEXT_OUT,
            ease: 'power3.in',
            stagger: TEXT_STAGGER,
        }, 0);

        //? image hand-off: incoming wipes in while the outgoing zooms away
        tl.set(incoming, {
            autoAlpha: 1,
            zIndex: 2,
            clipPath: direction === 'next' ? RIGHT_EDGE_CLIP : LEFT_EDGE_CLIP,
        }, 0);
        tl.set(outgoing, { zIndex: 1 }, 0);
        tl.to(incoming, { clipPath: FULL_CLIP, duration: IMAGE_DURATION, ease: EASE_IN_OUT }, 0);
        tl.fromTo(incoming.firstElementChild,
            { scale: 1.6, rotate: 12 * sign },
            { scale: 1, rotate: 0, duration: IMAGE_DURATION, ease: EASE_IN_OUT }, 0);
        tl.to(outgoing.firstElementChild,
            { scale: 1.6, rotate: -12 * sign, duration: IMAGE_DURATION, ease: EASE_IN_OUT }, 0);

        //? swap the content once the old text is gone; the layout effect animates it in
        tl.call(() => {
            activeIndexRef.current = nextIndex;
            setActiveIndex(nextIndex);
        }, null, TEXT_OUT + TEXT_STAGGER * textTargets.length);
        timelineRef.current = tl;
    }, []);

    const step = useCallback((direction) => {
        const current = activeIndexRef.current;
        const nextIndex = direction === 'next'
            ? (current + 1) % values.length
            : (current - 1 + values.length) % values.length;
        goTo(nextIndex, direction);
    }, [goTo]);

    //! ================= >>> AUTO-ADVANCE + PROGRESS <<< ============
    //* Re-armed on every change, so the loop never depends on a stale closure.
    useEffect(() => {
        activeIndexRef.current = activeIndex;

        highlightRefs.current.forEach((bar, index) => {
            if (!bar) return;
            gsap.killTweensOf(bar);
            if (index === activeIndex) {
                gsap.fromTo(bar, { scaleX: 0 }, { scaleX: 1, duration: VALUE_DURATION / 1000, ease: 'none' });
            } else {
                // stories-style: values already shown stay filled, upcoming ones stay empty
                gsap.to(bar, { scaleX: index < activeIndex ? 1 : 0, duration: 0.4, ease: EASE_OUT });
            }
        });

        timerRef.current = setTimeout(() => step('next'), VALUE_DURATION);
        return () => clearTimeout(timerRef.current);
    }, [activeIndex, step]);

    //? incoming text (also plays the intro on mount)
    useLayoutEffect(() => {
        const sign = directionRef.current === 'next' ? 1 : -1;
        const targets = [titleRef.current, ...lineRefs.current].filter(Boolean);
        gsap.fromTo(targets,
            { yPercent: 110 * sign, opacity: 0 },
            { yPercent: 0, opacity: 1, duration: 0.9, ease: EASE_OUT, stagger: 0.045, overwrite: 'auto' }
        );
    }, [activeIndex]);

    //? initial image stack: only the first layer visible
    useLayoutEffect(() => {
        const cursor = cursorRef.current;
        const layers = [...imageRefs.current];
        const bars = [...highlightRefs.current];

        layers.forEach((layer, index) => {
            gsap.set(layer, { autoAlpha: index === 0 ? 1 : 0, zIndex: index === 0 ? 2 : 0, clipPath: FULL_CLIP });
        });
        cursorMoveRef.current = {
            x: gsap.quickTo(cursor, 'x', { duration: 0.35, ease: 'power3.out' }),
            y: gsap.quickTo(cursor, 'y', { duration: 0.35, ease: 'power3.out' }),
        };
        return () => {
            clearTimeout(timerRef.current);
            timelineRef.current?.kill();
            gsap.killTweensOf([cursor, ...layers, ...bars]);
        };
    }, []);

    //! ================= >>> POINTER (prev / next halves) <<< ============
    const sideFor = (event) => {
        const rect = sectionRef.current.getBoundingClientRect();
        return event.clientX - rect.left < rect.width / 2 ? 'prev' : 'next';
    };

    const handleMouseMove = (event) => {
        const rect = sectionRef.current.getBoundingClientRect();
        const cursor = cursorRef.current;
        if (cursor && cursorMoveRef.current) {
            cursorMoveRef.current.x(event.clientX - rect.left - cursor.offsetWidth / 2);
            cursorMoveRef.current.y(event.clientY - rect.top - cursor.offsetHeight / 2);
        }
        if (cursorTextRef.current) {
            cursorTextRef.current.textContent = sideFor(event) === 'prev' ? 'Prev' : 'Next';
        }
    };

    const handleMouseEnter = () => gsap.to(cursorRef.current, { autoAlpha: 1, scale: 1, duration: 0.4, ease: EASE_OUT });
    const handleMouseLeave = () => gsap.to(cursorRef.current, { autoAlpha: 0, scale: 0.6, duration: 0.3, ease: 'power2.out' });
    const handleClick = (event) => step(sideFor(event));

    const value = values[activeIndex];

    return (
        <div
            className="values"
            ref={sectionRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            <div className="cursor" ref={cursorRef}>
                <p ref={cursorTextRef}>Next</p>
            </div>

            <div className="story-img">
                {values.map((item, index) => (
                    <div key={item.title} className="img" ref={(el) => { imageRefs.current[index] = el; }}>
                        <img src={item.image} alt={item.title} />
                    </div>
                ))}
            </div>

            <div className="story-content">
                <div className="row">
                    <div className="indices" aria-hidden="true">
                        {values.map((item, index) => (
                            <div key={item.title} className={`index ${index === activeIndex ? 'is-active' : ''}`}>
                                <div className="index-highlight" ref={(el) => { highlightRefs.current[index] = el; }}></div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="row">
                    <div className="profile">
                        <div className="profile-name desc-text font-bold leading-[0.9] text-white">
                            <p className="text-2xl" ref={titleRef}>{value.title}</p>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="title">
                        {value.text.map((line, index) => (
                            <div key={index} className="title-row text-white">
                                <h1 className="text-white" ref={(el) => { lineRefs.current[index] = el; }}>{line}</h1>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Values;
